import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ChatView } from '@lemoncloud/ssocio-chatbots-api';

import { useAgent } from '@codes/agents';
import { useDocument } from '@codes/chatbots';
import { useWebCoreStore } from '@codes/web-core';

import { ChatInterface, CommentsSection, ReportModal } from '../components';
import { useAgentChat } from '../hooks/useAgentChat';
import { transformDocumentData } from '../utils/document';

import type { DocumentData, DocumentResolver } from '../types/document';

interface ChatViewWithError extends ChatView {
    isError?: boolean;
}

export const AgentChatPage = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { profile } = useWebCoreStore();

    // State management
    const [openReportModal, setOpenReportModal] = useState(false);
    const [selectedValue, setSelectedValue] = useState('');
    const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
    const [documentResolver, setDocumentResolver] = useState<DocumentResolver | null>(null);

    // Data fetching
    const { data: selectedDocument, isLoading: isDocumentLoading } = useDocument(selectedDocId || '');
    const { data: agent, isLoading: isAgentLoading } = useAgent(id || '', !!id);
    const {
        currentChat,
        displayMessages,
        input,
        setInput,
        addMessage,
        isLoading: isChatLoading,
        isWaitingResponse,
        isMessagesLoading,
    } = useAgentChat({
        agentId: id || '',
    });

    // Explicitly type displayMessages
    const typedDisplayMessages: ChatViewWithError[] = displayMessages;

    // Derived values
    const agentName = agent?.meta$?.nick ?? agent?.id ?? t('agent.listItem.states.noName');

    // Transform document data for DocumentModal
    const transformedDocumentData = useMemo(
        () => transformDocumentData(selectedDocument, selectedDocId),
        [selectedDocument, selectedDocId]
    );

    // Document resolution effect
    useEffect(() => {
        if (selectedDocument && documentResolver) {
            const documentData = transformDocumentData(selectedDocument, selectedDocId);
            if (documentData) {
                documentResolver(documentData);
                setDocumentResolver(null);
            }
        }
    }, [selectedDocument, documentResolver, selectedDocId]);

    // Promise-based document selection handler
    const handleDocumentSelect = useCallback(async (documentId: string): Promise<DocumentData> => {
        return new Promise<DocumentData>(resolve => {
            setDocumentResolver(() => resolve);
            setSelectedDocId(documentId);
        });
    }, []);

    const handleDocumentClose = useCallback(() => {
        setSelectedDocId(null);
    }, []);

    // Event handlers
    const handleFeedback = (messageId: string, isPositive: boolean) => {
        // TODO: Implement feedback API call
        toast({ title: `Message ${messageId} feedback: ${isPositive ? 'positive' : 'negative'}` });
    };

    const handleRadioChange = (value: string) => {
        setSelectedValue(value);
    };

    const handleClose = () => {
        navigate('/agent-catalog');
    };

    const handleSubmit = () => {
        if (input.trim() && !isWaitingResponse) {
            addMessage(input);
        }
    };

    const handleEdit = () => {
        // TODO: Implement edit functionality
        console.log('Edit agent');
    };

    const handleHistory = () => {
        // TODO: Implement history functionality
        console.log('View history');
    };

    const handleReport = () => {
        setOpenReportModal(true);
    };

    const handleDelete = () => {
        // TODO: Implement delete functionality
        console.log('Delete comment');
    };

    const handleSubmitComment = (comment: string) => {
        // TODO: Implement comment submission
        console.log('Submit comment:', comment);
    };

    const handleSubmitReport = () => {
        // TODO: Implement report submission
        console.log('Submit report:', selectedValue);
        setOpenReportModal(false);
        setSelectedValue('');
    };

    if (isAgentLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="w-8 h-8 animate-spin rounded-full border-2 border-border border-t-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('agent.catalog.loading.agentInfo')}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-background flex-1 min-h-0 w-full flex flex-col">
                <div className="border-b border-detail-line font-medium py-3 px-8 max-md:py-4 max-md:px-4 max-md:bg-transparent max-md:border-none">
                    에이전트 채팅
                </div>
                <div className="flex-1 h-full min-h-0 py-8 px-7 max-md:px-4 max-md:pt-0 overflow-hidden flex">
                    <div className="flex-1 grid grid-cols-[2fr_1fr] max-md:grid-cols-1 gap-6 max-w-[1500px] w-full overflow-auto">
                        <ChatInterface
                            agentName={agentName}
                            messages={typedDisplayMessages}
                            input={input}
                            setInput={setInput}
                            onSubmit={handleSubmit}
                            isWaitingResponse={isWaitingResponse}
                            isLoading={isMessagesLoading}
                            currentChat={currentChat}
                            selectedDocumentId={selectedDocId}
                            selectedDocumentData={transformedDocumentData}
                            isDocumentLoading={isDocumentLoading}
                            onDocumentSelect={handleDocumentSelect}
                            onDocumentClose={handleDocumentClose}
                            onEdit={handleEdit}
                            onHistory={handleHistory}
                        />

                        <CommentsSection
                            onReport={handleReport}
                            onDelete={handleDelete}
                            onSubmitComment={handleSubmitComment}
                        />
                    </div>
                </div>
            </div>

            <ReportModal
                isOpen={openReportModal}
                onOpenChange={setOpenReportModal}
                selectedValue={selectedValue}
                onValueChange={handleRadioChange}
                onSubmit={handleSubmitReport}
            />
        </>
    );
};
